import { useState } from "react";
import { Button, ErrorMessage, Flex, Text, Textarea } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { AnswerSource, GenerationResultKind, QuestionType } from "~/graphql/supergraphEnums";
import { useAnswerDdqResponseQuestionsForProdOpsMutation } from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";
import type { GetDdqResponseForProdOpsQuery } from "~/graphql/supergraphTypes";

export type ProdOpsResponseQuestion =
  GetDdqResponseForProdOpsQuery["ddqResponseForProdOps"]["questions"][number];

export type QuestionAnswerRowProps = {
  arenaId: number;
  responseId: string;
  question: ProdOpsResponseQuestion;
};

const PROVENANCE_LABELS: Record<GenerationResultKind, string> = {
  [GenerationResultKind.AnswerBank]: "Previously answered",
  [GenerationResultKind.Generated]: "Generated by JunieAI",
  [GenerationResultKind.NoResponse]: "No response",
};

// The badge tracks current authorship, not just generationResultKind — editing an answer
// (source flips to USER) drops the AI-provenance label immediately, unlike the GP-facing page's
// policy of always keeping it after an edit.
const provenanceLabel = (question: ProdOpsResponseQuestion): string | null => {
  const { generationResultKind, answer } = question;
  if (generationResultKind === GenerationResultKind.NoResponse) {
    return PROVENANCE_LABELS[GenerationResultKind.NoResponse];
  }
  if (generationResultKind == null || answer?.source !== AnswerSource.System) {
    return null;
  }
  return PROVENANCE_LABELS[generationResultKind];
};

// Non-TEXT questions fall back to editing raw JSON of `value` — a working escape hatch, not a
// type-aware editor.
const seedEditValue = (question: ProdOpsResponseQuestion): string =>
  question.questionType === QuestionType.Text
    ? (question.answer?.valueText ?? "")
    : JSON.stringify(question.answer?.value ?? null, null, 2);

// Mirrors seedEditValue for the read-only view: a non-TEXT answer has nothing in valueText.
const displayValue = (question: ProdOpsResponseQuestion): string | null => {
  const { answer } = question;
  if (answer == null) return null;
  if (question.questionType === QuestionType.Text) return answer.valueText;
  return answer.value != null ? JSON.stringify(answer.value) : null;
};

export const QuestionAnswerRow = ({
  arenaId,
  responseId,
  question,
}: QuestionAnswerRowProps) => {
  const { answer } = question;
  const isTextQuestion = question.questionType === QuestionType.Text;
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const [answerQuestion, { loading: isSaving, error: saveError }] =
    useAnswerDdqResponseQuestionsForProdOpsMutation({
      context: withClientContext("federation"),
      refetchQueries: ["GetDdqResponseForProdOps", "GetDdqResponseProgressForProdOps"],
    });

  const startEditing = () => {
    setValidationError(null);
    setEditedValue(seedEditValue(question));
    setIsEditing(true);
  };

  const onSave = () => {
    setValidationError(null);
    let parsedValue: unknown;
    if (!isTextQuestion) {
      try {
        parsedValue = JSON.parse(editedValue);
      } catch {
        setValidationError("Not valid JSON.");
        return;
      }
    }
    answerQuestion({
      variables: {
        arenaId,
        input: {
          ddqResponseId: responseId,
          answers: [
            {
              questionId: question.id,
              textAnswer: isTextQuestion ? editedValue : undefined,
              value: isTextQuestion ? undefined : (parsedValue as Record<string, unknown>),
            },
          ],
        },
      },
    })
      .then(() => setIsEditing(false))
      .catch(() => {
        // Surfaced via the mutation's own `error` below; nothing further to do here.
      });
  };

  const label = provenanceLabel(question);

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.X_SMALL}
      className={styles.questionRow}
      data-testid={`ddq-answer-qa-question-${question.id}`}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap={SPACING.SMALL}>
        <Text variant="strong-sm">{question.questionText}</Text>
        {!isEditing && (
          <Button
            variant="neutral"
            onClick={startEditing}
            data-testid={`ddq-answer-qa-edit-${question.id}`}
          >
            Edit
          </Button>
        )}
      </Flex>

      {label && (
        <Text
          variant="regular-sm"
          color={Colors.Neutral600}
          data-testid={`ddq-answer-qa-provenance-${question.id}`}
        >
          {label}
        </Text>
      )}

      {isEditing ? (
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Textarea
            isFullWidth
            size="large"
            value={editedValue}
            onSetValue={setEditedValue}
            data-testid={`ddq-answer-qa-edit-field-${question.id}`}
          />
          {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
          {saveError && (
            <ErrorMessage data-testid={`ddq-answer-qa-edit-error-${question.id}`}>
              {saveError.message}
            </ErrorMessage>
          )}
          <Flex gap={SPACING.SMALL} justifyContent="flex-end">
            <Button variant="neutral" onClick={() => setIsEditing(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              isLoading={isSaving}
              onClick={onSave}
              data-testid={`ddq-answer-qa-save-${question.id}`}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      ) : (
        displayValue(question) && <Text variant="regular-md">{displayValue(question)}</Text>
      )}

      {!isEditing && answer != null && answer.sources.length > 0 && (
        <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.citationList}>
          {answer.sources.map((source) => (
            <Text
              key={source.id}
              variant="regular-sm"
              color={Colors.Neutral600}
              data-testid={`ddq-answer-qa-citation-${source.id}`}
            >
              {source.title}
              {source.snippet ? ` — ${source.snippet}` : ""}
            </Text>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
